import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import React from 'react';
import { createStyles, Theme } from '@/contexts/theme-context';
import { formatPercentage } from '@sales-portal/blocs/utils/money';
import { isValidNumber } from '@/utils/number';
import { PeerRange } from '@/experiences/sales-portal/domain/pricing/screens/management/blocs/transformers/use-pricing-management-transformers';
import { roundPercentageToNearestTenth } from '@/experiences/sales-portal/domain/pricing/screens/management/blocs/utils/variant-pack-cost-change-pricing';
import {
  selectPriceEdit,
  usePricingEditsStore,
} from '@sales-portal/domain/pricing/blocs/stores/pricing-edits-store';
import { Tooltip } from '@usepepper/ui/atoms/tooltip';
import { Typography } from '@usepepper/ui/atoms/typography';
import { View } from 'react-native';

const BAR_WIDTH = 64;
const BAR_THICKNESS = 1;
const MARKER_HEIGHT = 12;
const MARKER_THICKNESS = 4;

interface Props {
  grossMarginPercentage: number;
  peerRange: PeerRange | null;
  restaurantUUID: string;
  variantPackUUID: string;
}

const getMarkerColor = (
  currentValue: number,
  min: number,
  max: number,
  theme: Theme,
) => {
  // Round to the same precision used in the tooltip so the marker color
  // stays consistent with the displayed range values.
  const rv = roundPercentageToNearestTenth(currentValue);
  const rMin = roundPercentageToNearestTenth(min);
  const rMax = roundPercentageToNearestTenth(max);

  if (rv < rMin || rv > rMax) {
    return theme.sk.color.icon.negative.secondary;
  }

  const midpoint = (rMin + rMax) / 2;
  if (rv <= midpoint) {
    return theme.sk.color.icon.tertiary;
  }
  return theme.sk.color.icon.positive.secondary;
};

const getMarkerRangePercentage = (
  currentValue: number,
  min: number,
  max: number,
): number => {
  if (max === min) {
    return 0.5;
  }

  const clamped = Math.max(min, Math.min(max, currentValue));
  return (clamped - min) / (max - min);
};

export const PricingPeerRangeCell: React.FC<Props> = ({
  grossMarginPercentage,
  peerRange,
  restaurantUUID,
  variantPackUUID,
}) => {
  const [styles, theme] = useStyles();
  const editedGrossMargin = usePricingEditsStore(
    (state) =>
      selectPriceEdit(state, variantPackUUID, restaurantUUID)
        ?.grossMarginPercentage,
  );
  const currentValue = editedGrossMargin ?? grossMarginPercentage;

  const hasPeerRange =
    isValidNumber(peerRange?.minGrossProfitInPercentage) &&
    isValidNumber(peerRange?.maxGrossProfitInPercentage);

  const min = hasPeerRange ? peerRange.minGrossProfitInPercentage! : 0;
  const max = hasPeerRange ? peerRange.maxGrossProfitInPercentage! : 0;

  const percentage = getMarkerRangePercentage(currentValue, min, max);
  const markerLeft = percentage * (BAR_WIDTH - MARKER_THICKNESS);

  const animatedLeft = useSharedValue(markerLeft);
  animatedLeft.value = withTiming(markerLeft, { duration: 200 });

  const animatedMarkerStyle = useAnimatedStyle(() => ({
    left: animatedLeft.value,
  }));

  const markerColor = getMarkerColor(currentValue, min, max, theme);
  const tooltipText = `${formatPercentage(min, 1)} - ${formatPercentage(max, 1)}`;

  return hasPeerRange ? (
    <View
      accessibilityLabel={tooltipText}
      style={[
        styles.wrapper,
        Boolean(editedGrossMargin) ? styles.wrapperWithEdit : null,
      ]}
    >
      <Tooltip
        containerStyle={styles.tooltipContainer}
        offset={{ y: 8 }}
        placement="top"
        tabIndex={-1}
        trigger={
          <View style={styles.container}>
            <View style={styles.barContainer}>
              <View style={styles.endcap} />
              <View style={styles.bar} />
              <View style={styles.endcap} />
            </View>
            <Animated.View
              style={[
                styles.marker,
                { backgroundColor: markerColor },
                animatedMarkerStyle,
              ]}
              testID="pricing-peer-range-marker"
            />
          </View>
        }
      >
        <Typography color={theme.sk.color.text.ondark} variant="paragraph-sm">
          {tooltipText}
        </Typography>
      </Tooltip>
    </View>
  ) : null;
};

const useStyles = createStyles((theme) => ({
  bar: {
    backgroundColor: theme.sk.color.border.secondary,
    flex: 1,
    height: BAR_THICKNESS,
  },
  barContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    width: BAR_WIDTH,
  },
  container: {
    height: MARKER_HEIGHT,
    justifyContent: 'center',
    position: 'relative',
    width: BAR_WIDTH,
  },
  endcap: {
    backgroundColor: theme.sk.color.border.secondary,
    height: MARKER_HEIGHT,
    width: BAR_THICKNESS,
  },
  marker: {
    borderRadius: theme.sk.radii.radiusSmall,
    height: MARKER_HEIGHT,
    position: 'absolute',
    width: MARKER_THICKNESS,
  },
  tooltipContainer: {
    backgroundColor: theme.sk.color.bg.toast,
    paddingHorizontal: theme.sk.spacing.spaceSmall,
    paddingVertical: theme.sk.spacing.spaceSmall,
  },
  wrapper: {
    alignItems: 'flex-start',
  },
  wrapperWithEdit: {
    marginTop: theme.sk.spacing.spaceSmall,
  },
}));
